import { z } from 'zod';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import semver from 'semver';
import type { Entries, Replace } from 'type-fest';

import { type InterpretationType } from './tenant-configuration';

extendZodWithOpenApi(z);

export const SlideScanId = z.string().min(1).max(100).describe('The ID of the slide-scan');

export const TenantId = z.string().min(1).describe('The tenant id');
export type TenantIdType = z.infer<typeof TenantId>;

const slideScanVerifiedArr = ['CONFIRMED', 'RESCAN_REQUESTED', 'RESCANNED'] as const;
const slideScanUnverifiedArr = ['AI_PROCESSED'] as const;
export const SlideScanVerifiedStatus = z.enum(slideScanVerifiedArr);
export type SlideScanVerifiedStatusType = z.infer<typeof SlideScanVerifiedStatus>;
export const SlideScanUnverifiedStatus = z.enum(slideScanUnverifiedArr);
export const SlideScanStatus = z.enum([...slideScanVerifiedArr, ...slideScanUnverifiedArr]);
export type SlideScanStatusType = z.infer<typeof SlideScanStatus>;

export const SlideScanGeneralQcStatus = z.enum(['NOT_SET', 'GOOD', 'BAD']);
export type SlideScanGeneralQcStatusType = z.infer<typeof SlideScanGeneralQcStatus>;

export const AggregatedStatus = z.enum(['GREEN', 'YELLOW', 'RED']);
export type AggregatedStatusType = z.infer<typeof AggregatedStatus>;

export const CONSTANTS = {
  MAX_PAGE_SIZE: 1000,
  DEFAULT_PAGE_SIZE: 100,
  EVENTS_PARTITION_SCHEMA_VERSION: '1.0.0',
  RUM_EVENTS_PARTITION_SCHEMA_VERSION: '1.0.0',
};

export const DATE_HELPERS = {
  getDefaultStartDate: () => new Date(0),
  getDateNow: () => new Date(),
};

const AI_HANDLER_FILE_VERSION = (['v3', 'v3.1.0', 'v3.2.0', 'v3.3.0', 'v3.4.0'] as const).toSorted((a, b) =>
  semver.rcompare(semver.coerce(a) || '0.0.0', semver.coerce(b) || '0.0.0'),
);

export type AIHandlerFileVersion = (typeof AI_HANDLER_FILE_VERSION)[number];
export type AIHandlerKey<T extends AIHandlerFileVersion> = `aiResults${Uppercase<Replace<T, '.', '_', { all: true }>>}`;

type AIHandlerFiles = {
  [K in AIHandlerFileVersion as AIHandlerKey<K>]: `result_${K}.json`;
};

const getVersionFile = <T extends AIHandlerFileVersion>(version: T): `result_${T}.json` => `result_${version}.json`;
const getVersionKey = <T extends AIHandlerFileVersion>(version: T): AIHandlerKey<T> =>
  `aiResults${version.replaceAll('.', '_').toUpperCase()}` as AIHandlerKey<T>;

export const AI_HANDLER_FILES = AI_HANDLER_FILE_VERSION.reduce(
  (acc, version) => ({
    ...acc,
    [getVersionKey(version)]: getVersionFile(version),
  }),
  {} as AIHandlerFiles,
);
export const AI_HANDLER_FILE_NAMES = Object.values(AI_HANDLER_FILES);
export const AI_HANDLER_KEYS = Object.keys(AI_HANDLER_FILES) as Array<keyof AIHandlerFiles>;

export const KNOWN_FILE_NAMES = {
  metadata: 'metadata.json',
  aiLogs: 'logs.txt',
  generatedMetadata: 'slide-scan-generated-metadata.json',
  standardizedResults: 'result.json',
  additionalMetadata: 'additional_metadata.json',
  ...AI_HANDLER_FILES,
} as const satisfies Record<string, string>;

// Sorted from latest to oldest
export const SUPPORTED_AI_RESULTS = AI_HANDLER_FILE_VERSION.map(getVersionFile);

const [latestAIVersion] = AI_HANDLER_FILE_VERSION;

export const LATEST_AI_HANDLER_VERSION = semver.coerce(latestAIVersion)!.toString();
export const LATEST_AI_HANDLER_FILE_NAME = getVersionFile(latestAIVersion);
export const LATEST_AI_HANDLER_KEY = getVersionKey(latestAIVersion);

export type KnownFiles = (typeof KNOWN_FILE_NAMES)[keyof typeof KNOWN_FILE_NAMES];

export const configState = z.enum(['CREATED', 'ONBOARDED']);

export type ConfigStateType = z.infer<typeof configState>;

const AIDetectableSlideScanIssue330 = z.enum([
  'TISSUE_NOT_DETECTED',
  'OUT_OF_FOCUS_REGION',
  'MISALIGNED_COVER_SLIP',
  'CUT_OFF_TISSUE',
  'MARKER_ON_TOP_OF_TISSUE',
]).describe('The issues that can be detected by AI models up to result version 3.3.0');

const AIDetectableSlideScanIssue340 = z.enum([
  'IMAGE_DATA_LOSS',
  'STRIPING',
]).describe('The issues that can be detected by AI models from result version 3.3.0 to 3.4.0');

export const AIDetectableSlideScanIssue = z.enum([
  ...AIDetectableSlideScanIssue330.options,
  ...AIDetectableSlideScanIssue340.options,
]).describe('The issues that can be detected by AI models given the latest result version');

export type AIDetectableSlideScanIssueType = z.infer<typeof AIDetectableSlideScanIssue>;

export const AIDetectableSlideScanIssueMap = {
  blur: AIDetectableSlideScanIssue.enum.OUT_OF_FOCUS_REGION,
  missingTissue: AIDetectableSlideScanIssue.enum.TISSUE_NOT_DETECTED,
  misalignedCoverSlip: AIDetectableSlideScanIssue.enum.MISALIGNED_COVER_SLIP,
  cutOffTissue: AIDetectableSlideScanIssue.enum.CUT_OFF_TISSUE,
  markerOnTopOfTissue: AIDetectableSlideScanIssue.enum.MARKER_ON_TOP_OF_TISSUE,
  imageDataLoss: AIDetectableSlideScanIssue.enum.IMAGE_DATA_LOSS,
  striping: AIDetectableSlideScanIssue.enum.STRIPING,
} satisfies Record<keyof InterpretationType, AIDetectableSlideScanIssueType>;

export const AIModelToInterpretationMap = Object.fromEntries(
  (Object.entries(AIDetectableSlideScanIssueMap) as Entries<typeof AIDetectableSlideScanIssueMap>).map(
    ([key, value]) => [value, key] as const,
  ),
);

export const MacroOnlyIssues = [AIDetectableSlideScanIssue.enum.TISSUE_NOT_DETECTED];

export const NonAiDetectableSlideScanIssue = z.enum([
  'FOLDS',
  'MISALIGNED_IMAGE_STITCHING',
  'AIR_BUBBLES',
  'THICKNESS_VARIATION',
  'STAINING_INHOMOGENEITY',
  'FOREIGN_MATERIAL',
  'OTHER',
]);

export const SlideScanIssue = z.enum([
  ...AIDetectableSlideScanIssue.options,
  ...NonAiDetectableSlideScanIssue.options,
]);

export const SlideScanIssue330 = z.enum([
  ...AIDetectableSlideScanIssue330.options,
  ...NonAiDetectableSlideScanIssue.options,
]);

// When deprecating an issue make sure the type is:
// - handled correctly in the dashboard
// - removed from the barco config if it exists
export const deprecatedSlideScanIssues = z.enum([
  'TISSUE_OUTSIDE_MAXIMUM_SCANNING_AREA',
  'TISSUE_OUTSIDE_COVER_AREA',
  'STRIPES',
]);

export type SlideScanIssueType = z.infer<typeof SlideScanIssue>;
export type DeprecatedSlideScanIssueType = z.infer<typeof deprecatedSlideScanIssues>;

export const SlideScanVerifiedIssueStatus = z.enum([
  'FALSE_POSITIVE', // Issue was detected by AI, but rejected by user
  'FALSE_NEGATIVE', // Issue was not detected by AI, but added by user
  'TRUE_POSITIVE', // M or L Issue was detected by AI and confirmed by user
  'TRUE_NEGATIVE', // No issue was detected by AI and confirmed by user
  'ACCEPTED', // @deprecated Issue was detected by AI and accepted by user
  'USER_ONLY', // Issue was added by user, but AI is not configured for this type
  'IGNORED', // Issue was detected by AI and there AI decision was S -> Green Zone
]);
export type SlideScanVerifiedIssueStatusType = z.infer<typeof SlideScanVerifiedIssueStatus>;

export const SlideScanIssueDecision = z
  .enum(['S', 'M', 'L'])
  .describe('Small, Medium or Large, aka green yellow or red. Indicates the zone in which the issue is classified');
export type SlideScanIssueDecisionType = z.infer<typeof SlideScanIssueDecision>;

export const modelsConfigVersion = z.uuid().describe('Identifies the configuration that was saved by the user');
export type ModelsConfigVersionType = z.infer<typeof modelsConfigVersion>;

export const SlideScanDetectedIssue = z.object({
  slideScanId: SlideScanId.optional(),
  issueType: SlideScanIssue,
  isAIDetected: z.boolean(),
  isUserDetected: z.boolean(),
  aiIssueStatus: z.enum(['ACCEPTED', 'REJECTED']).optional(),
  score: z.number().optional().describe('Present if the issue was detected by AI only'),
  decision: SlideScanIssueDecision.optional().describe('Present if the issue was detected by AI only'),
});
export type SlideScanDetectedIssue = z.infer<typeof SlideScanDetectedIssue>;

export const UserIdSchema = z.uuid();

export const User = z.object({
  id: UserIdSchema,
  tenantId: TenantId,
  email: z.email(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
});
export type UserType = z.infer<typeof User>;

export const FilteredOutReason = z.enum(['UNSUPPORTED_SCANNER', 'UNSUPPORTED_Z_STACK']).describe('Identified reason for filtering out the slide scan');
export type FilteredOutReasonType = z.infer<typeof FilteredOutReason>;

export const SlideScanItem = z.object({
  id: SlideScanId,
  physicalId: z.string().optional().describe('The physical id of the slide scan, if available'),
  triggeredAt: z.iso
    .datetime({ offset: true })
    .optional()
    .describe('Date, time and zone the slide scan was triggered for processing'),
  detectedIssues: z.array(SlideScanDetectedIssue).optional().prefault([]).describe('Detected issues for slide scan'),
  verified: z.boolean().optional().prefault(false).describe('Indicates if the slide scan has been verified or not'),
  verifiedAt: z.iso.datetime({ offset: true }).optional().describe('Datetime that the slide scan was verified'),
  uploadedAt: z.iso.datetime({ offset: true }).optional().describe('Datetime that the slide scan became available'),
  wsiThumbnail: z
    .object({
      name: z.string().describe('The filename of the thumbnail, relatively to the path of the slide-scan.'),
      url: z.string().describe('The location of the wsi-thumbnail').optional(),
    })
    .optional()
    .describe('The WSI thumbnail that has been detected in the slide-scan'),
  macro: z
    .object({
      name: z.string().describe('The filename of the macro, relatively to the path of the slide-scan.'),
      url: z.string().describe('The location of the macro').optional(),
    })
    .optional()
    .describe('The macro that has been detected in the slide-scan'),
  label: z
    .object({
      name: z.string().describe('The filename of the label, relatively to the path of the slide-scan.'),
      url: z.string().describe('The location of the label').optional(),
    })
    .optional()
    .describe('The label that have been detected in the slide-scan'),
  patches: z
    .array(
      z.object({
        name: z.string().describe('The filename of the patch, relatively to the path of the slide-scan.'),
        url: z.string().describe('The location of the patch').optional(),
      }),
    )
    .min(5)
    .max(5)
    .optional()
    .describe('The list of AIPatchIssueOutFocusRegion patches'),
  scanner: z
    .object({
      type: z.string().max(1000).optional().describe('Type of scanner'),
      serialNumber: z.string().max(1000).optional().describe('Serial number of the scanner'),
    })
    .optional()
    .describe('Scanner information'),
  rackNumber: z.number().nonnegative().optional().describe('Rack number'),
  rackPosition: z.number().nonnegative().optional().describe('Rack position'),
  staining: z.string().optional().describe('Staining type of the slide'),
  customField1: z.string().optional().describe('Value for custom field 1'),
  comment: z.string().optional().describe('Comment regarding the verification of the slide scan'),
  aiResultUrl: z.string().optional().describe('DEPRECATED: The location of the ai results'), // @deprecated
  magnification: z.number().nonnegative().optional().describe('The magnification factor of the scan'),
  processingState: SlideScanStatus.describe('The processing state of the slide scan'),
  generalQcStatus: SlideScanGeneralQcStatus.describe('The general QC status of the slide scan'),
  lastUpdatedBy: z.string().optional().describe('The id of the user that last updated the slide scan'),
  lastUpdatedByUser: User.optional().describe('The user that last updated the slide scan'),
  aiProcessingStatus: z
    .enum(['SUCCESS', 'FAILED', ...FilteredOutReason.options])
    .describe('The status of the AI output.'),
  aggregatedStatus: AggregatedStatus.optional().describe(
    'The aggregated status of the slide scan. Defined if aiProcessingStatus is SUCCESS',
  ),
  isRescan: z.boolean().prefault(false).describe('Indication of whether this slide scan is a rescan of another one'),
  scanNumber: z.number().nonnegative().optional().describe('The scan number, if this is a rescan'),
  caseName: z.string().describe('the name of the case'),
  slideName: z.string().describe('the name of the slide'),
  slideScanDisplayName: z.string().optional().describe('The display name of the slide scan'),
  metadataUrl: z.string().optional().describe('The location of the metadata'),
  bodyPart: z.string().optional().describe('The body part of the slide scan'),
  generatedMetadataUrl: z.string().optional().describe('The location of the generated metadata'),
  modelsConfigVersion: modelsConfigVersion
    .optional()
    .describe('The version of the models configuration used for this slide scan'),
  isPhiDetected: z
    .boolean()
    .optional()
    .describe('Indicates if PHI was detected in the slide scan'),
});
export type SlideScanItemType = z.infer<typeof SlideScanItem>;
export type SlideScanItemAIProcessingStatusType = z.infer<typeof SlideScanItem.shape.aiProcessingStatus>;

export const DataplaneId = z.uuid().describe('Dataplane id');
export const AIPatchIssueOutFocusRegionPatches = [
  'patch_1.png',
  'patch_2.png',
  'patch_3.png',
  'patch_4.png',
  'patch_5.png',
];
export const MACRO_IMAGE_NAME = 'macro.png';
export const THUMBNAIL_IMAGE_NAME = 'thumbnail.png';
export const LABEL_IMAGE_NAME = 'label.png';

export const AWSCredentials = z.object({
  accessKey: z.string().describe('The access key for signing the urls'),
  secretAccessKey: z.string().describe('The secret access key for signing the urls'),
  sessionToken: z.string().describe('The session token for signing the urls'),
  expiration: z.iso.datetime().optional().describe('The expiration date of the credentials'),
});

export type AWSCredentials = z.infer<typeof AWSCredentials>;

export const BucketCredentials = AWSCredentials.extend({
  bucketName: z.string().describe('The name of the bucket that should be used to sign the urls'),
});

export type BucketCredentials = z.infer<typeof BucketCredentials>;

export const BucketCredentialsWithKey = BucketCredentials.extend({
  key: z.string().describe('The key of the file in the bucket'),
});

export type BucketCredentialsWithKey = z.infer<typeof BucketCredentialsWithKey>;

export const BucketCredentialsWithKeys = BucketCredentials.extend({
  keys: z.array(z.string()).describe('The keys of the files in the bucket'),
});

export type BucketCredentialsWithKeys = z.infer<typeof BucketCredentialsWithKeys>;

export const AWSRumCredentials = AWSCredentials.extend({
  appMonitorId: z.string().describe('The app monitor id for the RUM'),
});

export type AWSRumCredentials = z.infer<typeof AWSRumCredentials>;

export const DataplaneManagementCredentials = z.object({
  credentials: AWSCredentials.extend({
    expiration: z.iso.datetime().optional().describe('The expiration date of the credentials'),
  }),
  secretValueCredentials: AWSCredentials.extend({
    secretArn: z.string().describe('The ARN of the secret that contains the credentials'),
  }).optional(),
  roles: z.object({
    containerRegistryRole: z.string().describe('The role that can be assumed to access the ECR'),
    artifactRole: z.string().describe('The role that can be assumed to access the S3'),
  }),
  deploymentImageName: z.string(),
});

export type DataplaneManagementCredentials = z.infer<typeof DataplaneManagementCredentials>;

export const AnonymizedSlideScanId = z.uuid().describe('The ID of the anonymized slide-scan');

export const MacroOffset = z.object({
  x: z.number().prefault(0).describe('X offset'),
  y: z.number().prefault(0).describe('Y offset'),
});

export type MacroOffsetType = z.infer<typeof MacroOffset>;

export const WSIPassportBase = z.object({
  macroOffset: z.looseObject(MacroOffset.shape).optional().describe('Offset of the macro image'),
});

export type WSIPassportBaseType = z.infer<typeof WSIPassportBase>;

export const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
export const ALLOWED_FILE_TYPES_UPLOAD = ['.json', '.txt', '.png'] as const;

export type AllowedExtension = typeof ALLOWED_FILE_TYPES_UPLOAD[number];
export const isAllowedExtension = (ext: string): ext is AllowedExtension =>
  (ALLOWED_FILE_TYPES_UPLOAD as readonly string[]).includes(ext);

export const extToMaxFileSizeMap: Record<AllowedExtension, number> = {
  '.json': 100 * 1024 * 1024,
  '.png': 100 * 1024 * 1024,
  '.txt': 10 * 1024 * 1024,
};
